import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactSortable } from 'react-sortablejs'
import { FaArrowsAlt, FaChevronDown, FaCloudUploadAlt, FaEye, FaGripVertical, FaPlus, FaSearch, FaSearchPlus, FaTimes } from 'react-icons/fa'
import toast from 'react-hot-toast'
import config from '@/constant/config'
import { getBrands } from '@/store/slices/brand'
import { getCategories } from '@/store/slices/category'
import { getCharacters } from '@/store/slices/character'
import { getSeries } from '@/store/slices/series'
import { deleteHighlightImage, uploadHighlightImage } from '@/store/slices/image'
import {
  getHighlightSectionDetail,
  saveHighlightSection,
  updateHighlightSection
} from '@/store/slices/highlight-section'

import FullPageLoader from '@/components/FullPageLoader'
import Header from '@/components/Header'
import HighlightSectionPreview from '@/components/HighlightSectionPreview'
import Modal from '@/components/Modal'

const ATTRIBUTE_TYPES = ['CATEGORY', 'BRAND', 'SERIES', 'CHARACTER', 'CUSTOM']
const FILTER_ATTRIBUTE_TYPES = ['CATEGORY', 'BRAND', 'SERIES', 'CHARACTER']
const MAX_BACKGROUND_IMAGE_SIZE = 5 * 1024 * 1024
const DEFAULT_BACKGROUND_POSITION = { x: 50, y: 0 }
const MIN_BACKGROUND_ZOOM = 1
const MAX_BACKGROUND_ZOOM = 2.5
const DEFAULT_BACKGROUND_ZOOM = 1
const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const normalizeBackgroundZoom = value => Math.max(
  MIN_BACKGROUND_ZOOM,
  Math.min(MAX_BACKGROUND_ZOOM, Number(value) || DEFAULT_BACKGROUND_ZOOM)
)
const normalizeCustomThemeColor = value => {
  const color = value.trim()
  return HEX_COLOR_PATTERN.test(color) ? color.toUpperCase() : ''
}
const LAYOUTS = [
  { value: 'SIGNATURE_RAIL', label: 'Signature Rail', description: 'Bold title panel with a horizontal discovery rail.' },
  { value: 'SPOTLIGHT_MOSAIC', label: 'Spotlight Mosaic', description: 'A 2x2 hero paired with a landscape title card and supporting cards.' },
  { value: 'GALLERY_GRID', label: 'Gallery Grid', description: 'An even, editorial grid for broad discovery.' }
]
const THEMES = [
  { value: 'MULTI_YELLOW', label: 'Multi Yellow', classes: 'bg-[#fff4b8] border-[#212121]' },
  { value: 'ELECTRIC_BLUE', label: 'Electric Blue', classes: 'bg-[#dfeeff] border-[#183153]' },
  { value: 'CORAL_POP', label: 'Coral Pop', classes: 'bg-[#ffe2dc] border-[#7a263a]' },
  { value: 'CUSTOM', label: 'Custom', classes: 'border-gray-800 bg-[linear-gradient(135deg,#ff806d_0%,#ffe369_33%,#62a9f9_66%,#7a263a_100%)] text-white' }
]

const ATTRIBUTE_FETCHERS = {
  CATEGORY: getCategories,
  BRAND: getBrands,
  SERIES: getSeries,
  CHARACTER: getCharacters
}

const emptyFilters = () => ({ category: [], brand: [], series: [], character: [], search: '' })
const emptyFilterLabels = () => ({ category: [], brand: [], series: [], character: [] })
const itemKey = item => item.cardId || item.draftId || item.attributeId
const filterKey = type => type.toLowerCase()
const normalizeFilters = filters => ({
  category: [...new Set(filters?.category || [])],
  brand: [...new Set(filters?.brand || [])],
  series: [...new Set(filters?.series || [])],
  character: [...new Set(filters?.character || [])],
  search: filters?.search?.trim() || ''
})
const hasFilterDestination = filters => (
  FILTER_ATTRIBUTE_TYPES.some(type => filters?.[filterKey(type)]?.length) || !!filters?.search?.trim()
)

const HighlightSectionEditorPage = () => {
  const { id } = useParams()
  const isEdit = !!id
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [highlightTitle, setHighlightTitle] = useState('')
  const [backgroundImage, setBackgroundImage] = useState(null)
  const [attributeType, setAttributeType] = useState('CATEGORY')
  const [layout, setLayout] = useState('SIGNATURE_RAIL')
  const [theme, setTheme] = useState('MULTI_YELLOW')
  const [customThemeColor, setCustomThemeColor] = useState('')
  const [hidden, setHidden] = useState(false)
  const [items, setItems] = useState([])
  const [options, setOptions] = useState([])
  const [paging, setPaging] = useState({ page: 1, totalPage: 1 })
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingOptions, setIsFetchingOptions] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [previewMode, setPreviewMode] = useState(null)
  const [previewViewport, setPreviewViewport] = useState('DESKTOP')
  const [draftBackgroundPosition, setDraftBackgroundPosition] = useState(DEFAULT_BACKGROUND_POSITION)
  const [draftBackgroundZoom, setDraftBackgroundZoom] = useState(DEFAULT_BACKGROUND_ZOOM)
  const [filterCardKey, setFilterCardKey] = useState(null)
  const [filterDraft, setFilterDraft] = useState(emptyFilters())
  const [filterDraftLabels, setFilterDraftLabels] = useState(emptyFilterLabels())
  const [filterOptions, setFilterOptions] = useState({})
  const [filterPaging, setFilterPaging] = useState({})
  const [filterSearch, setFilterSearch] = useState({})
  const [filterLoading, setFilterLoading] = useState({})

  const newUploads = useRef(new Set())
  const pendingDeletes = useRef(new Set())
  const saved = useRef(false)
  const customCardCounter = useRef(0)

  useEffect(() => {
    if (!isEdit) return
    const fetchDetail = async () => {
      try {
        setIsLoading(true)
        const { data } = await dispatch(getHighlightSectionDetail(id)).unwrap()
        setTitle(data.title || '')
        setSubtitle(data.subtitle || '')
        setHighlightTitle(data.highlightTitle || data.title || '')
        setBackgroundImage(data.backgroundImage
          ? {
              ...data.backgroundImage,
              position: data.backgroundImage.position || DEFAULT_BACKGROUND_POSITION,
              zoom: normalizeBackgroundZoom(data.backgroundImage.zoom)
            }
          : null)
        setAttributeType(data.attributeType)
        setLayout(data.layout)
        // The selected theme is authoritative.  Older records may still carry
        // a custom color after switching back to a preset palette; do not let
        // that stale optional value force the editor back to CUSTOM.
        setTheme(data.theme || (data.customThemeColor ? 'CUSTOM' : 'MULTI_YELLOW'))
        setCustomThemeColor(data.customThemeColor || '')
        setHidden(data.hidden)
        setItems(data.items)
      } catch(err) {
        toast.error('Unable to load Highlight section')
        navigate(config.path.content.highlightSection)
      } finally {
        setIsLoading(false)
      }
    }
    fetchDetail()
  }, [id])

  useEffect(() => {
    if (attributeType === 'CUSTOM') return undefined
    const timer = setTimeout(() => fetchOptions(1, false), 250)
    return () => clearTimeout(timer)
  }, [attributeType, search])

  useEffect(() => {
    if (!filterCardKey) return undefined
    const timer = setTimeout(() => {
      FILTER_ATTRIBUTE_TYPES.forEach(type => fetchFilterOptions(type, 1, false))
    }, 250)
    return () => clearTimeout(timer)
  }, [filterCardKey, filterSearch])

  useEffect(() => () => {
    if (saved.current) return
    newUploads.current.forEach(filename => dispatch(deleteHighlightImage(filename)))
  }, [])

  useEffect(() => {
    if (!previewMode) return undefined
    const closeOnEscape = event => {
      if (event.key === 'Escape') setPreviewMode(null)
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [previewMode])

  const fetchOptions = async (page, append) => {
    if (attributeType === 'CUSTOM') return
    try {
      setIsFetchingOptions(true)
      const { data, paging: responsePaging } = await dispatch(ATTRIBUTE_FETCHERS[attributeType]({
        page,
        size: 20,
        search
      })).unwrap()
      setOptions(current => append ? [...current, ...data] : data)
      setPaging(responsePaging)
    } catch(err) {
      toast.error(`Unable to load ${attributeType.toLowerCase()} options`)
    } finally {
      setIsFetchingOptions(false)
    }
  }

  const changeType = nextType => {
    if (nextType === attributeType) return
    if (items.length && !window.confirm('Changing the attribute type will clear the selected cards. Continue?')) return
    items.forEach(item => queueImageRemoval(item.image?.filename))
    setItems([])
    setOptions([])
    setSearch('')
    setAttributeType(nextType)
  }

  const queueImageRemoval = filename => {
    if (!filename) return
    if (newUploads.current.has(filename)) {
      dispatch(deleteHighlightImage(filename))
      newUploads.current.delete(filename)
      return
    }
    pendingDeletes.current.add(filename)
  }

  const addAttribute = option => {
    if (items.some(item => item.attributeId === option.id)) return
    setItems(current => [...current, {
      attributeId: option.id,
      name: option.name,
      image: null
    }])
  }

  const addCustomCard = () => {
    customCardCounter.current += 1
    setItems(current => [...current, {
      draftId: `custom-${Date.now()}-${customCardCounter.current}`,
      title: '',
      image: null,
      filters: emptyFilters(),
      filterLabels: emptyFilterLabels()
    }])
  }

  const removeAttribute = item => {
    queueImageRemoval(item.image?.filename)
    setItems(current => current.filter(entry => itemKey(entry) !== itemKey(item)))
  }

  const uploadImage = async (item, file) => {
    if (!file) return
    try {
      setIsLoading(true)
      const form = new FormData()
      form.append('image', file)
      const { data } = await dispatch(uploadHighlightImage(form)).unwrap()
      queueImageRemoval(item.image?.filename)
      newUploads.current.add(data.name)
      setItems(current => current.map(entry => itemKey(entry) === itemKey(item)
        ? { ...entry, image: { filename: data.name, url: data.url } }
        : entry
      ))
    } catch(err) {
      toast.error('Unable to upload image')
    } finally {
      setIsLoading(false)
    }
  }

  const uploadBackgroundImage = async file => {
    if (!file) return
    if (file.size > MAX_BACKGROUND_IMAGE_SIZE) {
      toast.error('Background image must be 5 MB or smaller')
      return
    }
    try {
      setIsLoading(true)
      const form = new FormData()
      form.append('image', file)
      const { data } = await dispatch(uploadHighlightImage(form)).unwrap()
      queueImageRemoval(backgroundImage?.filename)
      newUploads.current.add(data.name)
      setBackgroundImage({
        filename: data.name,
        url: data.url,
        position: DEFAULT_BACKGROUND_POSITION,
        zoom: DEFAULT_BACKGROUND_ZOOM
      })
    } catch(err) {
      toast.error('Unable to upload background image')
    } finally {
      setIsLoading(false)
    }
  }

  const removeBackgroundImage = () => {
    queueImageRemoval(backgroundImage?.filename)
    setBackgroundImage(null)
  }

  const fetchFilterOptions = async (type, page, append) => {
    const key = filterKey(type)
    try {
      setFilterLoading(current => ({ ...current, [key]: true }))
      const { data, paging: responsePaging } = await dispatch(ATTRIBUTE_FETCHERS[type]({
        page,
        size: 20,
        search: filterSearch[key] || ''
      })).unwrap()
      setFilterOptions(current => ({
        ...current,
        [key]: append ? [...(current[key] || []), ...data] : data
      }))
      setFilterPaging(current => ({ ...current, [key]: responsePaging }))
    } catch(err) {
      toast.error(`Unable to load ${key} filters`)
    } finally {
      setFilterLoading(current => ({ ...current, [key]: false }))
    }
  }

  const openFilterModal = item => {
    setFilterCardKey(itemKey(item))
    setFilterDraft(normalizeFilters(item.filters))
    setFilterDraftLabels({ ...emptyFilterLabels(), ...(item.filterLabels || {}) })
    setFilterOptions({})
    setFilterPaging({})
    setFilterSearch({})
  }

  const closeFilterModal = () => {
    setFilterCardKey(null)
  }

  const toggleFilterOption = (type, option) => {
    const key = filterKey(type)
    setFilterDraft(current => ({
      ...current,
      [key]: current[key].includes(option.id)
        ? current[key].filter(id => id !== option.id)
        : [...current[key], option.id]
    }))
    setFilterDraftLabels(current => ({
      ...current,
      [key]: current[key].some(entry => entry.id === option.id)
        ? current[key].filter(entry => entry.id !== option.id)
        : [...current[key], { id: option.id, name: option.name }]
    }))
  }

  const applyFilters = () => {
    const filters = normalizeFilters(filterDraft)
    setItems(current => current.map(item => itemKey(item) === filterCardKey
      ? { ...item, filters, filterLabels: filterDraftLabels }
      : item
    ))
    closeFilterModal()
  }

  const openPreview = mode => {
    setPreviewViewport('DESKTOP')
    if (mode === 'POSITION') {
      setDraftBackgroundPosition(backgroundImage?.position || DEFAULT_BACKGROUND_POSITION)
      setDraftBackgroundZoom(normalizeBackgroundZoom(backgroundImage?.zoom))
    }
    setPreviewMode(mode)
  }

  const closePreview = () => {
    setPreviewMode(null)
  }

  const applyBackgroundPosition = () => {
    setBackgroundImage(current => current
      ? { ...current, position: draftBackgroundPosition, zoom: normalizeBackgroundZoom(draftBackgroundZoom) }
      : current)
    closePreview()
  }

  const submit = async e => {
    e.preventDefault()
    setHasSubmitted(true)
    if (title.length > 80) {
      toast.error('Section title must be 80 characters or fewer')
      return
    }
    if (subtitle.length > 160) {
      toast.error('Section subtitle must be 160 characters or fewer')
      return
    }
    if (!highlightTitle.trim() || highlightTitle.length > 80) {
      toast.error(!highlightTitle.trim() ? 'Highlight title is required' : 'Highlight title must be 80 characters or fewer')
      return
    }
    if (theme === 'CUSTOM' && !normalizeCustomThemeColor(customThemeColor)) {
      toast.error('Custom background must be a valid hex color')
      return
    }
    if (!items.length) {
      toast.error(attributeType === 'CUSTOM' ? 'Add at least one custom card' : 'Select at least one attribute')
      return
    }
    if (items.some(item => !item.image?.filename || !item.image?.url)) {
      toast.error('Upload an image for every selected attribute')
      return
    }
    if (attributeType === 'CUSTOM' && items.some(item => !item.title?.trim() || item.title.length > 80 || !hasFilterDestination(item.filters))) {
      toast.error('Every custom card needs a title and at least one filter or keyword')
      return
    }

    const body = {
      title: title.trim(),
      subtitle: subtitle.trim(),
      highlightTitle: highlightTitle.trim(),
      attributeType,
      layout,
      theme,
      customThemeColor: theme === 'CUSTOM' ? normalizeCustomThemeColor(customThemeColor) : '',
      hidden,
      backgroundImage,
      items: attributeType === 'CUSTOM'
        ? items.map(item => ({
            ...(item.cardId ? { cardId: item.cardId } : {}),
            title: item.title.trim(),
            image: item.image,
            filters: normalizeFilters(item.filters)
          }))
        : items.map(item => ({
            attributeId: item.attributeId,
            image: item.image
          }))
    }

    try {
      setIsLoading(true)
      if (isEdit) {
        await dispatch(updateHighlightSection({ id, body })).unwrap()
      } else {
        await dispatch(saveHighlightSection(body)).unwrap()
      }

      saved.current = true
      newUploads.current.clear()
      await Promise.allSettled([...pendingDeletes.current].map(filename => dispatch(deleteHighlightImage(filename)).unwrap()))
      toast.success(`Highlight section ${isEdit ? 'updated' : 'created'}`)
      navigate(config.path.content.highlightSection)
    } catch(err) {
      toast.error('Unable to save Highlight section')
      setIsLoading(false)
    }
  }

  const selectedIds = new Set(items.map(item => item.attributeId))
  const titleError = hasSubmitted && title.length > 80 ? 'Use 80 characters or fewer.' : ''
  const subtitleError = hasSubmitted && subtitle.length > 160 ? 'Use 160 characters or fewer.' : ''
  const highlightTitleError = hasSubmitted && !highlightTitle.trim()
    ? 'Highlight title is required.'
    : ''
  const customThemeColorError = theme === 'CUSTOM' && !normalizeCustomThemeColor(customThemeColor)
    ? 'Enter a hex color such as #FF806D.'
    : ''
  const customCardError = item => {
    if (!hasSubmitted || attributeType !== 'CUSTOM') return ''
    if (!item.title?.trim()) return 'Card title is required.'
    if (item.title.length > 80) return 'Use 80 characters or fewer.'
    if (!hasFilterDestination(item.filters)) return 'Choose a filter or enter a keyword.'
    return ''
  }
  const isPositioningBackground = previewMode === 'POSITION'
  const previewBackgroundImage = isPositioningBackground && backgroundImage
    ? {
        ...backgroundImage,
        position: draftBackgroundPosition,
        zoom: draftBackgroundZoom
      }
    : backgroundImage
  return (
    <Header title={`${isEdit ? 'Edit' : 'Create'} Highlight Section`}>
      {isLoading && <FullPageLoader />}

      <Modal visibleState={[!!previewMode, closePreview]} width="w-[calc(100vw-2rem)] max-w-[1600px]">
        <div className="sticky top-0 z-50 -mx-1 flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white px-1 pb-4">
          <div>
            <h2 className="text-xl font-bold">{isPositioningBackground ? 'Adjust background placement' : 'Storefront preview'}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {isPositioningBackground
                ? 'Drag the background in the complete storefront layout, or use the arrow keys for precise movement.'
                : 'Previewing your current unsaved changes.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="flex rounded-full bg-gray-100 p-1" aria-label="Preview viewport">
              {[
                { value: 'DESKTOP', label: 'Desktop · 1440px' },
                { value: 'MOBILE', label: 'Mobile · 390px' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPreviewViewport(option.value)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition ${previewViewport === option.value ? 'bg-gray-800 text-white shadow-sm' : 'text-gray-600 hover:bg-white'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {isPositioningBackground && (
              <>
                <span className="rounded-full bg-gray-100 px-4 py-2 text-xs font-bold text-gray-700">
                  X {Math.round(draftBackgroundPosition.x)}% · Y {Math.round(draftBackgroundPosition.y)}%
                </span>
                <label className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700">
                  <FaSearchPlus aria-hidden="true" />
                  <span>Zoom {Math.round(draftBackgroundZoom * 100)}%</span>
                  <input
                    type="range"
                    min={MIN_BACKGROUND_ZOOM}
                    max={MAX_BACKGROUND_ZOOM}
                    step="0.05"
                    value={draftBackgroundZoom}
                    onChange={event => setDraftBackgroundZoom(normalizeBackgroundZoom(event.target.value))}
                    aria-label="Background image zoom"
                    className="w-24 accent-gray-800"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setDraftBackgroundPosition(DEFAULT_BACKGROUND_POSITION)}
                  className="rounded-full border border-gray-300 px-4 py-2 text-xs font-semibold hover:bg-gray-50"
                >
                  Reset position
                </button>
                <button
                  type="button"
                  onClick={() => setDraftBackgroundZoom(DEFAULT_BACKGROUND_ZOOM)}
                  className="rounded-full border border-gray-300 px-4 py-2 text-xs font-semibold hover:bg-gray-50"
                >
                  Reset zoom
                </button>
              </>
            )}
            <button
              type="button"
              onClick={closePreview}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100"
              aria-label={`Close ${isPositioningBackground ? 'background placement' : 'storefront preview'}`}
            >
              <FaTimes />
            </button>
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
          <HighlightSectionPreview
            title={title}
            subtitle={subtitle}
            highlightTitle={highlightTitle}
            layout={layout}
            theme={theme}
            customThemeColor={theme === 'CUSTOM' ? normalizeCustomThemeColor(customThemeColor) : ''}
            backgroundImage={previewBackgroundImage}
            items={items}
            viewport={previewViewport}
            positioning={isPositioningBackground}
            onBackgroundPositionChange={setDraftBackgroundPosition}
          />
        </div>
        {isPositioningBackground && (
          <div className="sticky bottom-0 z-50 -mx-1 mt-4 flex justify-end gap-3 border-t border-gray-200 bg-white px-1 pt-4">
            <button type="button" className="rounded-full border border-gray-800 px-6 py-2.5 font-semibold" onClick={closePreview}>Cancel</button>
            <button type="button" className="rounded-full bg-gray-800 px-7 py-2.5 font-semibold text-white" onClick={applyBackgroundPosition}>Apply position</button>
          </div>
        )}
      </Modal>

      <Modal visibleState={[!!filterCardKey, closeFilterModal]} width="w-[calc(100vw-2rem)] max-w-4xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-4">
          <div>
            <h2 className="text-xl font-bold">Configure card filters</h2>
            <p className="mt-1 text-sm text-gray-500">Match any selection within a type, and all populated types plus the keyword.</p>
          </div>
          <button type="button" onClick={closeFilterModal} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100" aria-label="Close filters"><FaTimes /></button>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {FILTER_ATTRIBUTE_TYPES.map(type => {
            const key = filterKey(type)
            const selected = filterDraft[key] || []
            const selectedLabels = filterDraftLabels[key] || []
            const page = filterPaging[key]
            return (
              <section key={type} className="rounded-2xl border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="font-bold capitalize">{key}</h3>
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold">{selected.length} selected</span>
                </div>
                <input
                  value={filterSearch[key] || ''}
                  onChange={event => setFilterSearch(current => ({ ...current, [key]: event.target.value }))}
                  placeholder={`Search ${key}`}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-800"
                />
                {!!selectedLabels.length && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedLabels.map(option => (
                      <button key={option.id} type="button" onClick={() => toggleFilterOption(type, option)} className="rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white">
                        {option.name} <span aria-hidden="true">×</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3 max-h-40 overflow-y-auto rounded-xl border border-gray-200">
                  {(filterOptions[key] || []).map(option => {
                    const isSelected = selected.includes(option.id)
                    return (
                      <button key={option.id} type="button" onClick={() => toggleFilterOption(type, option)} className={`flex w-full items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-0 ${isSelected ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                        <span className="truncate">{option.name}</span>
                        <span className="text-xs font-semibold">{isSelected ? 'Selected' : <FaPlus />}</span>
                      </button>
                    )
                  })}
                  {filterLoading[key] && <div className="p-3 text-center text-xs text-gray-500">Loading…</div>}
                </div>
                {page?.page < page?.totalPage && (
                  <button type="button" onClick={() => fetchFilterOptions(type, page.page + 1, true)} className="mt-3 w-full rounded-xl border border-gray-300 py-2 text-xs font-semibold hover:bg-gray-50">Load more</button>
                )}
              </section>
            )
          })}
        </div>
        <label className="mt-5 grid gap-2">
          <span className="flex items-center justify-between gap-3 font-semibold"><span>Keyword <span className="text-xs font-normal text-gray-500">(optional)</span></span><span className="text-xs font-normal text-gray-500">{filterDraft.search?.length || 0}/160</span></span>
          <input value={filterDraft.search || ''} maxLength={160} onChange={event => setFilterDraft(current => ({ ...current, search: event.target.value }))} placeholder="Search product names" className="rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-gray-800" />
        </label>
        {!hasFilterDestination(filterDraft) && <p className="mt-3 text-sm font-medium text-red-600">Choose at least one filter or enter a keyword.</p>}
        <div className="mt-5 flex justify-end gap-3 border-t border-gray-200 pt-4">
          <button type="button" onClick={closeFilterModal} className="rounded-full border border-gray-800 px-6 py-2.5 font-semibold">Cancel</button>
          <button type="button" disabled={!hasFilterDestination(filterDraft)} onClick={applyFilters} className="rounded-full bg-gray-800 px-7 py-2.5 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">Apply filters</button>
        </div>
      </Modal>

      <form onSubmit={submit} className="grid gap-8">
        <div className="sticky top-0 z-10 -mx-1 flex items-center justify-end gap-3 rounded-2xl border border-gray-200 bg-white/95 px-4 py-3 shadow-md backdrop-blur">
          <button
            type="button"
            className="flex items-center gap-2 rounded-full border border-gray-800 px-6 py-2.5 font-semibold hover:bg-gray-50"
            onClick={() => openPreview('PREVIEW')}
          >
            <FaEye />
            Preview
          </button>
          <button type="button" className="rounded-full border border-gray-800 px-6 py-2.5 font-semibold" onClick={() => navigate(config.path.content.highlightSection)}>Cancel</button>
          <button type="submit" className="rounded-full bg-gray-800 px-7 py-2.5 font-semibold text-white">{isEdit ? 'Save changes' : 'Create section'}</button>
        </div>

        <section className="grid gap-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm lg:grid-cols-2">
          <label className="grid content-start gap-2">
            <span className="flex items-center justify-between gap-3 font-semibold">
              <span>Section title <span className="text-xs font-normal text-gray-500">(optional)</span></span>
              <span className="text-xs font-normal text-gray-500">{title.length}/80</span>
            </span>
            <input
              value={title}
              maxLength={80}
              onChange={e => setTitle(e.target.value)}
              placeholder="Explore by Category"
              aria-invalid={!!titleError}
              className={`rounded-xl border px-4 py-3 outline-none focus:border-gray-800 ${titleError ? 'border-red-500' : 'border-gray-300'}`}
            />
            {titleError && <span className="text-xs font-medium text-red-600">{titleError}</span>}
          </label>

          <label className="grid content-start gap-2">
            <span className="flex items-center justify-between gap-3 font-semibold">
              <span>Section subtitle <span className="text-xs font-normal text-gray-500">(optional)</span></span>
              <span className="text-xs font-normal text-gray-500">{subtitle.length}/160</span>
            </span>
            <input
              value={subtitle}
              maxLength={160}
              onChange={e => setSubtitle(e.target.value)}
              placeholder="EXPLORE BRAND"
              aria-invalid={!!subtitleError}
              className={`rounded-xl border px-4 py-3 outline-none focus:border-gray-800 ${subtitleError ? 'border-red-500' : 'border-gray-300'}`}
            />
            {subtitleError && <span className="text-xs font-medium text-red-600">{subtitleError}</span>}
          </label>

          <label className="grid content-start gap-2">
            <span className="font-semibold">Attribute type</span>
            <div className="relative w-full max-w-sm">
              <select
                value={attributeType}
                onChange={e => changeType(e.target.value)}
                className="h-11 w-full appearance-none rounded-xl border border-gray-300 bg-white px-4 pr-10 outline-none focus:border-gray-800"
              >
                {ATTRIBUTE_TYPES.map(type => <option key={type} value={type}>{type[0] + type.slice(1).toLowerCase()}</option>)}
              </select>
              <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500" />
            </div>
          </label>

          <label className="grid content-start gap-2">
            <span className="font-semibold">Layout</span>
            <div className="relative w-full max-w-sm">
              <select value={layout} onChange={e => setLayout(e.target.value)} className="h-11 w-full appearance-none rounded-xl border border-gray-300 bg-white px-4 pr-10 outline-none focus:border-gray-800">
                {LAYOUTS.map(entry => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
              </select>
              <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500" />
            </div>
            <span className="text-xs text-gray-500">{LAYOUTS.find(entry => entry.value === layout)?.description}</span>
          </label>

          <label className="grid content-start gap-2">
            <span className="flex items-center justify-between gap-3 font-semibold">
              <span>Highlight title <span className="text-red-500">*</span></span>
              <span className="text-xs font-normal text-gray-500">{highlightTitle.length}/80</span>
            </span>
            <input
              value={highlightTitle}
              maxLength={80}
              onChange={e => setHighlightTitle(e.target.value)}
              placeholder="Figures worth the spotlight"
              aria-invalid={!!highlightTitleError}
              className={`rounded-xl border px-4 py-3 outline-none focus:border-gray-800 ${highlightTitleError ? 'border-red-500' : 'border-gray-300'}`}
            />
            <span className={highlightTitleError ? 'text-xs font-medium text-red-600' : 'text-xs text-gray-500'}>
              {highlightTitleError || (layout === 'SIGNATURE_RAIL'
                ? 'Displayed inside the left feature card.'
                : layout === 'SPOTLIGHT_MOSAIC'
                  ? 'Displayed beside the Spotlight hero card.'
                  : 'Displayed inside the first Gallery Grid feature card.')}
            </span>
          </label>

          <div className="grid content-start gap-2">
            <span className="font-semibold">Theme</span>
            <div className="flex flex-wrap gap-3">
              {THEMES.map(entry => (
                <button
                  type="button"
                  key={entry.value}
                  onClick={() => {
                    setTheme(entry.value)
                    setCustomThemeColor(current => entry.value === 'CUSTOM' ? (normalizeCustomThemeColor(current) || '#7A263A') : '')
                  }}
                  className={`rounded-xl border-2 px-4 py-3 text-sm font-semibold ${entry.classes} ${theme === entry.value ? 'ring-2 ring-gray-800 ring-offset-2' : ''}`}
                >
                  {entry.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-500">Choose a palette for the title card, cards, and accents.</span>
            {theme === 'CUSTOM' && (
            <label className="mt-2 grid content-start gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <span className="flex items-center justify-between gap-3 font-semibold">
              <span>Custom background <span className="text-xs font-normal text-gray-500">(optional)</span></span>
              {customThemeColor && (
                <button
                  type="button"
                  onClick={() => setCustomThemeColor('')}
                  className="text-xs font-semibold text-gray-600 underline hover:text-gray-900"
                >
                  Reset
                </button>
              )}
            </span>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={normalizeCustomThemeColor(customThemeColor) || '#FFF4B8'}
                onChange={event => setCustomThemeColor(event.target.value.toUpperCase())}
                className="h-12 w-14 cursor-pointer rounded-xl border border-gray-300 bg-white p-1"
                aria-label="Choose custom section background color"
              />
              <input
                value={customThemeColor}
                onChange={event => setCustomThemeColor(event.target.value)}
                placeholder="#FFF4B8"
                maxLength={7}
                spellCheck="false"
                aria-invalid={!!customThemeColorError}
                className={`h-12 min-w-0 flex-1 rounded-xl border px-4 font-mono uppercase outline-none focus:border-gray-800 ${customThemeColorError ? 'border-red-500' : 'border-gray-300'}`}
              />
            </div>
            <span className={customThemeColorError ? 'text-xs font-medium text-red-600' : 'text-xs text-gray-500'}>
              {customThemeColorError || 'Overrides only this section’s background while preserving the selected palette.'}
            </span>
          </label>
            )}
          </div>

          <div className="grid content-start gap-3 lg:col-span-2">
            <div>
              <span className="font-semibold">Background image <span className="text-xs font-normal text-gray-500">(optional)</span></span>
              <p className="mt-1 text-xs text-gray-500">The source image is shown uncropped below. Use Adjust placement to position it against the complete storefront layout. Maximum 5 MB.</p>
            </div>

            {backgroundImage?.url ? (
              <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                  <span className="text-sm font-bold">Source image</span>
                  <span className="text-xs font-semibold text-gray-500">
                    X {Math.round(backgroundImage.position?.x ?? 50)}% · Y {Math.round(backgroundImage.position?.y ?? 0)}% · Zoom {Math.round(normalizeBackgroundZoom(backgroundImage.zoom) * 100)}%
                  </span>
                </div>
                <div className="flex min-h-[180px] items-center justify-center bg-[linear-gradient(135deg,#f8fafc_25%,#eef2f7_25%,#eef2f7_50%,#f8fafc_50%,#f8fafc_75%,#eef2f7_75%)] bg-[length:24px_24px] p-4">
                  <img src={backgroundImage.url} alt="Background source" className="max-h-64 max-w-full object-contain shadow-sm" />
                </div>
                <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => openPreview('POSITION')}
                    className="flex items-center gap-2 rounded-full bg-gray-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-700"
                  >
                    <FaArrowsAlt />
                    Adjust placement
                  </button>
                  <label className="cursor-pointer rounded-full border border-gray-300 px-5 py-2.5 text-sm font-semibold hover:bg-gray-50">
                    Replace image
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0]
                        e.target.value = ''
                        uploadBackgroundImage(file)
                      }}
                    />
                  </label>
                  <button type="button" onClick={removeBackgroundImage} className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 text-red-600 hover:bg-red-50" aria-label="Remove background image">
                    <FaTimes />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex min-h-[120px] w-full max-w-2xl cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100">
                <FaCloudUploadAlt className="text-3xl" />
                <span className="text-sm font-semibold">Upload background image</span>
                <span className="text-xs">PNG, JPG, or WebP · Maximum 5 MB</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    uploadBackgroundImage(file)
                  }}
                />
              </label>
            )}
          </div>

          <label className="flex items-center gap-3">
            <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} className="h-5 w-5" />
            <span><strong>Hide section</strong><br /><small className="text-gray-500">Keep it saved without showing it on the storefront.</small></span>
          </label>
        </section>

        <section className="grid gap-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm lg:grid-cols-[minmax(260px,0.8fr)_minmax(0,1.8fr)]">
          {attributeType === 'CUSTOM' ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5">
            <h2 className="text-lg font-bold">Custom cards</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-500">Create a card with its own title, image, and product-search filters. You can reuse filters across cards.</p>
            <button type="button" onClick={addCustomCard} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gray-800 px-4 py-3 text-sm font-semibold text-white hover:bg-gray-700"><FaPlus /> Add custom card</button>
          </div>
          ) : (
          <div>
            <h2 className="text-lg font-bold">Choose {attributeType.toLowerCase()} cards</h2>
            <div className="relative mt-4">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${attributeType.toLowerCase()}`}
                className="w-full rounded-xl border border-gray-300 py-3 pl-11 pr-4"
              />
            </div>

            <div className="mt-3 max-h-[430px] overflow-y-auto rounded-xl border border-gray-200">
              {options.map(option => {
                const selected = selectedIds.has(option.id)
                return (
                  <button
                    type="button"
                    key={option.id}
                    disabled={selected}
                    onClick={() => addAttribute(option)}
                    className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50 disabled:bg-green-50"
                  >
                    <span className="flex-1 truncate">{option.name}</span>
                    <span className="text-xs font-semibold text-gray-500">{selected ? 'Selected' : <FaPlus />}</span>
                  </button>
                )
              })}
              {isFetchingOptions && <div className="p-4 text-center text-sm text-gray-500">Loading…</div>}
            </div>
            {paging.page < paging.totalPage && (
              <button type="button" className="mt-3 w-full rounded-xl border border-gray-300 py-2 text-sm font-semibold" onClick={() => fetchOptions(paging.page + 1, true)}>
                Load more
              </button>
            )}
          </div>
          )}

          <div>
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-lg font-bold">{attributeType === 'CUSTOM' ? 'Custom cards' : 'Selected cards'}</h2>
                <p className="text-sm text-gray-500">{attributeType === 'CUSTOM' ? 'Set each card title, image, filters, and display order.' : 'Upload one image per card, then drag to set its display order.'}</p>
              </div>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold">{items.length} selected</span>
            </div>

            {!items.length && (
              <div className="mt-4 rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center text-gray-500">
                {attributeType === 'CUSTOM' ? 'Add a custom card from the left to build this section.' : 'Select attributes from the left to build this section.'}
              </div>
            )}

            {!!items.length && (
              <ReactSortable className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" list={items} setList={setItems} handle=".highlight-card-handle">
                {items.map((item, index) => (
                  <article key={itemKey(item)} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="relative aspect-[4/3] bg-gray-50">
                      {item.image?.url
                        ? <img src={item.image.url} alt="" className="h-full w-full object-contain p-3" />
                        : <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400"><FaCloudUploadAlt className="text-3xl" /><span className="text-xs">Image required</span></div>
                      }
                      <span className="absolute left-2 top-2 rounded-full bg-gray-900 px-2 py-1 text-xs text-white">#{index + 1}</span>
                    </div>
                    <div className="grid gap-3 p-4">
                      <div className="flex items-center gap-2">
                        <button type="button" className="highlight-card-handle cursor-move text-gray-400" aria-label="Drag card"><FaGripVertical /></button>
                        {attributeType === 'CUSTOM' ? (
                          <input value={item.title} maxLength={80} onChange={event => setItems(current => current.map(entry => itemKey(entry) === itemKey(item) ? { ...entry, title: event.target.value } : entry))} placeholder="Card title" aria-invalid={!!customCardError(item)} className={`min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm font-semibold outline-none focus:border-gray-800 ${customCardError(item) ? 'border-red-500' : 'border-gray-300'}`} />
                        ) : <strong className="min-w-0 flex-1 truncate">{item.name}</strong>}
                        <button type="button" onClick={() => removeAttribute(item)} className="text-red-500" aria-label={`Remove ${item.title || item.name}`}><FaTimes /></button>
                      </div>
                      <label className="cursor-pointer rounded-xl border border-dashed border-gray-400 px-3 py-2 text-center text-xs font-semibold hover:bg-gray-50">
                        {item.image ? 'Replace image' : 'Upload image'}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0]
                            e.target.value = ''
                            uploadImage(item, file)
                          }}
                        />
                      </label>
                      {attributeType === 'CUSTOM' && (
                        <>
                          <button type="button" onClick={() => openFilterModal(item)} className="rounded-xl border border-gray-800 px-3 py-2 text-xs font-semibold hover:bg-gray-50">Configure filters</button>
                          <div className="flex flex-wrap gap-1.5">
                            {FILTER_ATTRIBUTE_TYPES.flatMap(type => (item.filterLabels?.[filterKey(type)] || []).map(option => <span key={`${type}-${option.id}`} className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold">{option.name}</span>))}
                            {!!item.filters?.search && <span className="rounded-full bg-gray-900 px-2 py-1 text-[11px] font-semibold text-white">“{item.filters.search}”</span>}
                            {!hasFilterDestination(item.filters) && <span className="text-xs font-medium text-red-600">Choose filters</span>}
                          </div>
                          {customCardError(item) && <p className="text-xs font-medium text-red-600">{customCardError(item)}</p>}
                        </>
                      )}
                    </div>
                  </article>
                ))}
              </ReactSortable>
            )}
          </div>
        </section>

      </form>
    </Header>
  )
}

export default HighlightSectionEditorPage
